import React, { useEffect, useMemo, useState } from "react";
import { Box, Container } from "@mui/material";
import { useFetchDetail, usePostDetail } from "@hooks/useFetchDetail";
import { useLocation, useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import HeroCst from "@components/HeroCst";
import BackgroundImageCst from "@components/BackgroundImageCst";
import Breadcrumb from "@components/Breadcrumb";
import { useTranslation } from "react-i18next";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { useAddEditVirtualEdition } from "../hooks/useAddEditVirtualEdition";
import AnimationFadeIn from "@components/AnimationFadeIn";
import EditionFormCst from "@components/EditionFormCst";
import TeamsLimitAlertCst from "@components/TeamsLimitAlertCst";

const AddVirtualEditions: React.FC = () => {
  const { t } = useTranslation(["virtual-editions-page"]);
  const { id } = useParams();
  const location = useLocation();
  const { state: locationState } = useLocation();
  const [activityDuration, setActivityDuration] = useState(
    locationState?.duration || 0,
  );
  const [showModalWarning, setShowModalWarning] = useState(false);

  const { columns, mutate } = useFetchDetail(
    ServicesURL.getEditions,
    undefined,
    PagesID["corporate.virtual-editions"],
  );

  const navigate = useNavigate();
  const { columns: sessionColumns } = useFetchDetail(
    ServicesURL.getEditions,
    undefined,
    PagesID["corporate.virtual-editions.add"],
  );

  const { trigger } = usePostDetail(
    ServicesURL.postEditions + `/virtual?corporateId=${id}`,
    { onError: () => {} },
  );

  const { dataDetail: activityDetail, isLoading: isLoadingActivity } =
    useFetchDetail(
      locationState?.id
        ? `${ServicesURL.getActivities.replace(":idCorporate", `${id}`)}`
        : "",
      locationState?.id || "",
      PagesID["catalog.activities"],
    );

  const dataLocation = useMemo(
    () =>
      locationState
        ? {
            learning_objects: {
              id: locationState.id,
              label: locationState.title,
            },
          }
        : undefined,
    [locationState],
  );

  const {
    onSubmit,
    setOpenConfirmStatus,
    openConfirmStatus,
    handleAssociation,
    setOpenAssociation,
    handleDelete,
    getValues,
    getDisabled,
    openAssociations,
    facultiesAssociation,
    setFacultiesAssociation,
    tutorsAssociation,
    producerAssociation,
    presenterAssociation,
    coorganizerAssociation,
    cohostAssociation,
    setProducerAssociation,
    setPresenterAssociation,
    setCoorganizerAssociation,
    setCohostAssociation,
    setTutorsAssociation,
    resetSessionAssociations,
    hasDifferentDuration,
    savedInputForm,
    savedSessions,
  } = useAddEditVirtualEdition(
    trigger,
    id,
    dataLocation,
    mutate,
    activityDuration,
  );

  const inputsException = (col: any, inputForm: any) => {
    return col;
  };

  useEffect(() => {
    if (hasDifferentDuration) {
      setShowModalWarning(true);
    }
  }, [hasDifferentDuration]);

  return (
    <AnimationFadeIn>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-corp-editions-virtual.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={t("add.hero-title")}
          description={t("add.hero-description")}
        />
        <Box sx={{ mb: "2rem" }}>
          <TeamsLimitAlertCst corporateId={id} />
        </Box>
        {columns && (!locationState || !isLoadingActivity) && (
          <Box
            sx={{
              mt: "25px",
            }}
          >
            <EditionFormCst
              testIdPrefix="edition-virtual-add-form"
              isVirtual={true}
              isAdd={true}
              row={
                locationState
                  ? {
                      learning_objects: {
                        id: locationState.id,
                        label: locationState.title,
                      },
                      max_capacity: activityDetail?.max_partecipant,
                    }
                  : {}
              }
              columns={columns}
              sessionColumns={sessionColumns}
              onSubmit={onSubmit}
              inputsException={inputsException}
              backButton={() => {
                if (locationState) {
                  navigate(-1);
                } else {
                  navigate(`/corporate/${id}/detail/virtual-editions`);
                }
              }}
              setOpenAssociation={(accessorKey, open) =>
                setOpenAssociation(accessorKey, open)
              }
              handleDelete={(accessorKey, el) => handleDelete(accessorKey, el)}
              handleAssociation={(accessorKey, value) =>
                handleAssociation(accessorKey, value)
              }
              getValues={(accessorKey) => getValues(accessorKey)}
              getDisabled={(accessorKey) => getDisabled(accessorKey)}
              association={openAssociations}
              resetSessionAssociations={resetSessionAssociations}
              facultiesAssociation={facultiesAssociation}
              tutorsAssociation={tutorsAssociation}
              producerAssociation={producerAssociation}
              presenterAssociation={presenterAssociation}
              coorganizerAssociation={coorganizerAssociation}
              cohostAssociation={cohostAssociation}
              setProducerAssociation={setProducerAssociation}
              setPresenterAssociation={setPresenterAssociation}
              setCoorganizerAssociation={setCoorganizerAssociation}
              setCohostAssociation={setCohostAssociation}
              setFacultiesAssociation={setFacultiesAssociation}
              setTutorsAssociation={setTutorsAssociation}
              setActivityDuration={setActivityDuration}
            />
          </Box>
        )}
        <ModalConfirmsCst
          open={!!openConfirmStatus}
          title={t("outcome-item", { ns: "modal" })}
          description={t("outcome-item", { ns: "modal" })}
          onCallBackClosed={() => setOpenConfirmStatus(undefined)}
          status={openConfirmStatus}
        />
        <ModalConfirmsCst
          open={showModalWarning}
          title={t("duration-warning.title")}
          description={t("duration-warning.description")}
          routeToSamePage={true}
          onActionConfirmed={() => {
            setShowModalWarning(false);
            savedSessions && onSubmit(savedInputForm, savedSessions, true);
          }}
          onCallBackClosed={() => {
            setShowModalWarning(false);
          }}
        />
      </Container>
    </AnimationFadeIn>
  );
};

export default AddVirtualEditions;
